import { createSelector } from '@reduxjs/toolkit';
import { Book, Heart, Home } from 'lucide-preact';
import { useDispatch } from 'react-redux';

import BellNotifications from '@/components/BellNotifications';
import BurgerMenu from '@/components/BurgerMenu';
import TalkNavItem from '@/components/Header/TalkNavItem';
import NavItem from '@/components/NavBar/NavItem';
import NavMenuContent from '@/components/NavBar/NavMenuContent';
import NavMenuFooter from '@/components/NavBar/NavMenuFooter';
import { useShouldDisableJSRouting } from '@/components/providers/DisableJSRoutingProvider';
import ReferralButton from '@/components/ReferralButton';
import { loadPage } from '@/helpers';
import { AppState, useSelector } from '@/redux/helper';
import { selectCanAccessEducatorsApp, selectIsFreeUser, selectIsReferralParticipant } from '@/redux/selectors/users';
import { setEducatorsAppVisited } from '@/redux/slices/settings/visitedLinks/action';

import UserMenu from '../Menu';

const selectCanSeeReferralButton = createSelector(
  selectIsReferralParticipant,
  selectIsFreeUser,
  (state: AppState) => state.user.persona,
  (isReferralParticipant, isFreeUser, persona) => isReferralParticipant && (!isFreeUser || persona === 'educator'),
);

const AuthenticatedHeaderContent = ({ isLargeScreen }: { isLargeScreen: boolean }) => {
  const dispatch = useDispatch();
  const canAccessEducatorsApp = useSelector(selectCanAccessEducatorsApp);
  const educatorsAppURL = useSelector((state) => state.remoteConfig.educatorsAppURL);
  const isFreeUser = useSelector(selectIsFreeUser);
  const shouldDisableJSRouting = useShouldDisableJSRouting();
  const canSeeReferralButton = useSelector(selectCanSeeReferralButton);
  const educatorsAppVisited = useSelector((state) => state.settings.visitedLinks.educatorsApp);

  const educatorsAppLink = isLargeScreen
    ? educatorsAppURL
    : (() => {
        if (!educatorsAppURL) {
          return undefined;
        }
        const url = new URL(educatorsAppURL);
        url.pathname = '/about';
        return url.toString();
      })();

  return isLargeScreen ? (
    <>
      <nav>
        <ul className="preflight preflight-ul flex select-none items-center gap-6">
          <li>
            {canAccessEducatorsApp ? (
              <NavItem
                avoidInnerPadding
                text="Educator Resources"
                url={educatorsAppLink}
                customIcon={<Book className="size-5 text-stone-500 group-hover:text-stone-900" />}
                data-tour="educator-resources"
                className={
                  educatorsAppVisited
                    ? undefined
                    : 'animate-pulsingGradientText bg-[linear-gradient(to_right,#374151,#374151,#065f46,#047857,#059669,#10b981,#34d399,#6ee7b7,#34d399,#10b981,#059669,#047857,#065f46,#374151,#374151)] bg-[size:300%_100%] bg-clip-text text-transparent'
                }
                onClick={() => {
                  dispatch(setEducatorsAppVisited(true));
                }}
              />
            ) : (
              <NavItem avoidInnerPadding text="Home" url="/home" customIcon={<Home className="size-5" />} />
            )}
          </li>
          <li>
            <NavItem
              avoidInnerPadding
              text="My Animals"
              url="/habitats?tab=favorites"
              customIcon={<Heart className="size-5" />}
            />
          </li>
          <li>
            <TalkNavItem avoidInnerPadding />
          </li>
        </ul>
      </nav>
      <div className="ml-9 flex items-center gap-4">
        {canSeeReferralButton ? (
          <ReferralButton />
        ) : isFreeUser ? (
          <div>
            <a
              data-native={shouldDisableJSRouting}
              href="/plans"
              onClick={(e) => {
                if (shouldDisableJSRouting) {
                  e.preventDefault();
                  loadPage('/plans');
                }
              }}
              className="preflight preflight-a min-w-0 rounded-full bg-primary-green px-4 py-2 text-sm/4 font-medium text-white hover:bg-secondary-green"
            >
              Upgrade
            </a>
          </div>
        ) : null}
        <BellNotifications />
        <UserMenu />
      </div>
    </>
  ) : (
    <div className="ml-4 flex items-center gap-4">
      <BellNotifications />
      <BurgerMenu>
        <div>
          <NavMenuContent />
          <NavMenuFooter className="my-3" />
        </div>
      </BurgerMenu>
    </div>
  );
};

export default AuthenticatedHeaderContent;
